import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Camera, ChevronDown, Sparkles, X } from 'lucide-react';
import PrimaryButton from '../components/PrimaryButton';
import { plantsApi, aiApi, photosApi } from '../services/gardenApi';
import { convertHeicToJpeg, isHeicFile } from '../../utils/convertHeic';
import { useGardenPageTitle } from '../useGardenPageTitle';
import { GARDEN_IDENTIFY_PHOTO_STATE_KEY } from '../constants';

function currentSeasonGuess() {
  const m = new Date().getMonth();
  if (m >= 2 && m <= 4) return 'spring';
  if (m >= 5 && m <= 7) return 'summer';
  if (m >= 8 && m <= 10) return 'fall';
  return 'winter';
}

const PLANT_TYPES = ['tree', 'shrub', 'herb', 'vegetable', 'lawn', 'annual', 'perennial', 'other'];
const SUN_NEEDS = ['full sun', 'part sun', 'part shade', 'full shade'];
const WATER_NEEDS = ['low', 'medium', 'high'];
const HEALTH = ['thriving', 'healthy', 'struggling', 'dormant'];
const MONTHS = [
  { v: '',   l: '—' },
  { v: 1,    l: 'Jan' }, { v: 2, l: 'Feb' }, { v: 3, l: 'Mar' }, { v: 4, l: 'Apr' },
  { v: 5,    l: 'May' }, { v: 6, l: 'Jun' }, { v: 7, l: 'Jul' }, { v: 8, l: 'Aug' },
  { v: 9,    l: 'Sep' }, { v: 10, l: 'Oct' }, { v: 11, l: 'Nov' }, { v: 12, l: 'Dec' },
];

// Matches the SECTIONS map on the server (server/routes/garden/ai.js) so a
// "section" request only fills that chunk of the form. Label is what we
// show in progress + error toasts.
const SECTION_LABELS = {
  basics:     'Basics',
  appearance: 'Appearance',
  care:       'Care',
  seasonal:   'Seasonal timing',
  garden:     'In my garden',
};
function emptyForm() {
  return {
    common_name: '', botanical_name: '', nickname: '', plant_type: '', variety: '',
    bloom_color: '', bloom_month_start: '', bloom_month_end: '', foliage_notes: '',
    is_evergreen: null,
    sun_needs: '', water_needs: '', soil_preference: '', hardiness_zone: '',
    mature_height: '', mature_width: '',
    prune_month: '', prune_notes: '', fertilise_month: '', divide_month: '', mulch_month: '',
    dormancy_start: '', dormancy_end: '',
    spring_tasks: '', summer_tasks: '', fall_tasks: '', winter_tasks: '',
    location_notes: '', date_planted: '', health_status: '',
    is_pet_safe: null, is_pollinator_friendly: null, is_native: null, is_drought_tolerant: null,
  };
}

// Normalizes plant record returned from API into form state (null -> '' for strings,
// keeps booleans, ISO date -> yyyy-mm-dd).
function toForm(plant) {
  const f = emptyForm();
  for (const k of Object.keys(f)) {
    let v = plant[k];
    if (v == null) {
      if (['is_evergreen','is_pet_safe','is_pollinator_friendly','is_native','is_drought_tolerant'].includes(k)) {
        f[k] = null;
      } else {
        f[k] = '';
      }
      continue;
    }
    if (k === 'date_planted') {
      f[k] = typeof v === 'string' ? v.slice(0, 10) : new Date(v).toISOString().slice(0, 10);
    } else {
      f[k] = v;
    }
  }
  return f;
}

// Converts form state back into a PATCH/POST body, coercing numeric-looking fields
// and sending null rather than empty strings for nullable DB columns.
function toBody(form) {
  const out = {};
  for (const [k, v] of Object.entries(form)) {
    if (k.endsWith('_month_start') || k.endsWith('_month_end') ||
        k === 'bloom_month_start' || k === 'bloom_month_end' ||
        k === 'prune_month' || k === 'fertilise_month' || k === 'divide_month' ||
        k === 'mulch_month' || k === 'dormancy_start' || k === 'dormancy_end') {
      out[k] = v === '' || v == null ? null : Number(v);
      continue;
    }
    if (['is_evergreen','is_pet_safe','is_pollinator_friendly','is_native','is_drought_tolerant'].includes(k)) {
      out[k] = v; // bool | null
      continue;
    }
    if (typeof v === 'string') {
      const t = v.trim();
      out[k] = t === '' ? null : t;
      continue;
    }
    out[k] = v;
  }
  return out;
}

export default function PlantFormScreen() {
  const { plantId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const editing = !!plantId;

  const [form, setForm] = useState(emptyForm());
  /** Helps copy explain whether the user is typing a common or scientific name. */
  const [nameEntryMode, setNameEntryMode] = useState('common');
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Photo upload support. In edit mode we upload immediately against the
  // existing plant id; in create mode we stash the file and upload after
  // the plant is saved. `existingPhotos` lets us show the most recent
  // photo thumbnail for edit mode.
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [pendingPhoto, setPendingPhoto] = useState(null); // File for create-mode
  const [pendingPreview, setPendingPreview] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState(null);
  const photoInputRef = useRef(null);
  const identifyPhotoHandoffApplied = useRef(false);

  useGardenPageTitle(
    editing ? (form.common_name?.trim() || 'Edit plant') : 'Add a plant',
    editing ? 'Edit plant' : 'Add a plant',
  );

  // Prefill from /garden/plants/new?common_name=…&botanical_name=… (e.g. photo identify handoff).
  useEffect(() => {
    if (editing) return;
    const c = searchParams.get('common_name');
    const b = searchParams.get('botanical_name');
    if (!c && !b) return;
    setForm((f) => ({
      ...f,
      ...(c ? { common_name: c } : {}),
      ...(b ? { botanical_name: b } : {}),
    }));
    if (b && !c) setNameEntryMode('scientific');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apply URL prefill once on mount
  }, []);

  // Attach identify-from-photo image as pending upload so it saves with the new plant.
  useEffect(() => {
    if (editing || identifyPhotoHandoffApplied.current) return;
    const handoff = location.state?.[GARDEN_IDENTIFY_PHOTO_STATE_KEY];
    if (!handoff || !(handoff instanceof File)) return;
    identifyPhotoHandoffApplied.current = true;
    setPendingPhoto(handoff);
    setPendingPreview(URL.createObjectURL(handoff));
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true, state: {} },
    );
  }, [editing, location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    if (!editing) return;
    (async () => {
      try {
        const r = await plantsApi.get(plantId);
        setForm(toForm(r.plant));
        setExistingPhotos(r.photos || []);
      } catch (e) {
        setError(e.message || 'Failed to load plant');
      } finally {
        setLoading(false);
      }
    })();
  }, [editing, plantId]);

  useEffect(() => {
    if (!pendingPreview) return undefined;
    return () => URL.revokeObjectURL(pendingPreview);
  }, [pendingPreview]);

  const mostRecentPhoto = useMemo(() => {
    if (!existingPhotos.length) return null;
    return [...existingPhotos].sort((a, b) => {
      const ta = new Date(a.taken_at || a.created_at || 0).getTime();
      const tb = new Date(b.taken_at || b.created_at || 0).getTime();
      return tb - ta;
    })[0];
  }, [existingPhotos]);

  const handlePhotoPicked = async (e) => {
    let file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setPhotoError(null);

    if (isHeicFile(file)) {
      try {
        file = await convertHeicToJpeg(file);
      } catch (err) {
        setPhotoError(err.message || 'Could not convert HEIC image. Please try a JPG or PNG.');
        return;
      }
    }

    if (editing) {
      setPhotoBusy(true);
      try {
        const r = await photosApi.upload(plantId, file, {
          season: currentSeasonGuess(),
        });
        if (r.photo) setExistingPhotos((prev) => [r.photo, ...prev]);
      } catch (err) {
        setPhotoError(err.message || 'Could not upload photo');
      } finally {
        setPhotoBusy(false);
      }
      return;
    }

    setPendingPhoto(file);
    setPendingPreview(URL.createObjectURL(file));
  };

  const clearPendingPhoto = () => {
    setPendingPhoto(null);
    setPendingPreview(null);
  };

  const canSave = useMemo(() => !!form.common_name.trim(), [form.common_name]);
  const canAI = useMemo(
    () => !!(form.common_name.trim() || form.botanical_name.trim()),
    [form.common_name, form.botanical_name]
  );

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Merge AI results into form state, only filling fields the user hasn't
  // already set. Booleans treat null as "not yet set" and overwrite; strings
  // only overwrite empty strings; numbers only overwrite empty strings.
  // Returns the number of fields that were actually filled.
  const mergeAiIntoForm = (suggested) => {
    let filled = 0;
    setForm((prev) => {
      const next = { ...prev };
      for (const [k, v] of Object.entries(suggested || {})) {
        if (!(k in next)) continue;
        const current = next[k];
        const isBool = typeof v === 'boolean';
        const isEmptyCurrent =
          current === '' || current === null || current === undefined;
        if (isEmptyCurrent || (isBool && current === null)) {
          next[k] = v;
          filled += 1;
        }
      }
      return next;
    });
    return filled;
  };

  // Map AI errors onto friendly toast text.
  const describeAiError = (e, fallback) => {
    const code = e?.code;
    if (code === 'rate_limited' || e?.status === 429) {
      return e?.message && String(e.message).length < 200
        ? e.message
        : 'AI is busy right now \u2014 please try again in a minute.';
    }
    if (code === 'not_found')     return "AI couldn\u2019t identify that plant. Try a more specific name.";
    if (code === 'too_generic')   return 'That name was too general \u2014 try a more specific plant name.';
    if (code === 'not_configured')return 'AI lookup is not configured on the server.';
    if (code === 'ai_unavailable')return 'AI is temporarily unavailable. Please try again shortly.';
    if (code === 'daily_limit')   return e?.message || 'Daily AI limit reached. Try again tomorrow.';
    return e?.message || fallback || 'AI lookup failed. Try editing manually.';
  };

  // Global "is any AI call running" plus which section is active (for scoped
  // spinners on the per-section buttons). `null` means the big button is the
  // one running (or nothing is).
  const [aiBusy, setAiBusy] = useState(false);
  const [aiActiveSection, setAiActiveSection] = useState(null);
  const [aiMessage, setAiMessage] = useState(null);

  // Fill just one section. Used by the per-section buttons and sequenced by
  // the big button below. Throws on error so the caller can decide whether
  // to keep going.
  const fillSectionFromAI = async (sectionKey) => {
    const r = await aiApi.plantLookup({
      common_name: form.common_name.trim(),
      botanical_name: form.botanical_name.trim() || undefined,
      section: sectionKey,
      hardiness_zone: form.hardiness_zone.trim() || undefined,
    });
    const filled = mergeAiIntoForm(r.plant || {});
    return { filled, confidence: r.confidence || null };
  };

  const fillOneSection = async (sectionKey) => {
    if (!canAI || aiBusy) return;
    setAiBusy(true);
    setAiActiveSection(sectionKey);
    setAiMessage(null);
    try {
      const { filled, confidence } = await fillSectionFromAI(sectionKey);
      if (filled === 0) {
        setAiMessage({
          tone: 'ok',
          text: `AI didn\u2019t find anything new for ${SECTION_LABELS[sectionKey]} \u2014 already looks complete.`,
        });
      } else {
        const note = confidence === 'low' ? ' Confidence was low, so please double-check.' : '';
        setAiMessage({
          tone: 'ok',
          text: `Filled in ${filled} ${SECTION_LABELS[sectionKey]} detail${filled === 1 ? '' : 's'} from AI.${note}`,
        });
      }
    } catch (e) {
      setAiMessage({
        tone: 'err',
        text: describeAiError(e, `Couldn\u2019t fill ${SECTION_LABELS[sectionKey]}.`),
      });
    } finally {
      setAiBusy(false);
      setAiActiveSection(null);
    }
  };

  // Big "fill everything" — one full plant-lookup (no `section`) so Gemini
  // is called once per tap instead of five times in a row (avoids 429 bursts).
  const [aiProgress, setAiProgress] = useState(null); // sectionKey | null (per-section AI only)
  const fillFromAI = async () => {
    if (!canAI || aiBusy) return;
    setAiBusy(true);
    setAiMessage(null);
    setAiProgress(null);
    setAiActiveSection(null);
    try {
      const r = await aiApi.plantLookup({
        common_name: form.common_name.trim(),
        botanical_name: form.botanical_name.trim() || undefined,
        hardiness_zone: form.hardiness_zone.trim() || undefined,
      });
      const confidence = r.confidence || null;
      const totalFilled = mergeAiIntoForm(r.plant || {});
      if (totalFilled === 0) {
        setAiMessage({
          tone: 'ok',
          text: 'AI checked this plant \u2014 your details already look complete. Nothing to add.',
        });
      } else {
        const confNote = confidence === 'low' ? ' Confidence was low, so please double-check.' : '';
        setAiMessage({
          tone: 'ok',
          text: `Filled in ${totalFilled} detail${totalFilled === 1 ? '' : 's'} from AI.${confNote}`,
        });
      }
    } catch (e) {
      setAiMessage({
        tone: 'err',
        text: describeAiError(e, 'AI lookup failed. Try editing manually.'),
      });
    } finally {
      setAiBusy(false);
      setAiActiveSection(null);
      setAiProgress(null);
    }
  };

  const submit = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    setError(null);
    try {
      const body = toBody(form);
      if (editing) {
        await plantsApi.patch(plantId, body);
        navigate(`/garden/plants/${plantId}`);
      } else {
        const r = await plantsApi.create(body);
        const newId = r.plant.id;
        // If the user attached a photo before saving a new plant, push it
        // up now against the created record. A failed upload shouldn't
        // lose the plant itself — surface a soft warning but still
        // continue to the detail screen.
        if (pendingPhoto) {
          try {
            await photosApi.upload(newId, pendingPhoto, {
              season: currentSeasonGuess(),
            });
          } catch (photoErr) {
            console.warn('[garden] photo upload after create failed:', photoErr);
          }
        }
        navigate(`/garden/plants/${newId}`, { replace: true });
      }
    } catch (e) {
      setError(e.message || 'Failed to save');
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="px-4 py-6 text-sm text-garden-muted">Loading…</div>;
  }

  return (
    <div className="pb-32">
      <header className="sticky top-0 z-10 bg-garden-surface px-2 pt-3 pb-2 border-b border-transparent">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="min-h-11 min-w-11 rounded-full flex items-center justify-center text-garden-muted"
            aria-label="Back"
          >
            <ArrowLeft size={22} />
          </button>
          <h1 className="flex-1 text-lg font-semibold text-garden-ink truncate">
            {editing ? 'Edit plant' : 'Add a plant'}
          </h1>
        </div>
        {aiMessage ? (
          <div
            role="alert"
            className={
              'mt-2 text-xs rounded-xl px-3 py-2.5 leading-snug ' +
              (aiMessage.tone === 'ok'
                ? 'bg-garden-primaryLight text-garden-primaryDark'
                : aiMessage.tone === 'err'
                  ? 'bg-red-50 text-red-800 border border-red-100'
                  : 'bg-black/5 text-garden-ink')
            }
          >
            {aiMessage.text}
          </div>
        ) : null}
      </header>

      {error ? (
        <div className="mx-4 mb-3 p-3 rounded-xl bg-red-50 text-sm text-red-700">{error}</div>
      ) : null}

      <div className="px-4 space-y-3">
        <div className="rounded-2xl bg-white border border-black/5 p-4">
          <div className="font-semibold text-garden-ink mb-3">Photo</div>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*,image/heic,image/heif,.heic,.heif"
            capture="environment"
            className="hidden"
            onChange={handlePhotoPicked}
          />
          <div className="flex gap-3 items-start">
            <button
              type="button"
              onClick={() => photoInputRef.current?.click()}
              disabled={photoBusy}
              className="relative w-28 h-28 rounded-2xl bg-garden-primaryLight border border-garden-ink/10 flex items-center justify-center shrink-0 overflow-hidden disabled:opacity-60"
              aria-label={
                pendingPreview || mostRecentPhoto?.url ? 'Change photo' : 'Add photo'
              }
            >
              {pendingPreview ? (
                <img src={pendingPreview} alt="" className="w-full h-full object-contain" />
              ) : mostRecentPhoto?.url ? (
                <img
                  src={mostRecentPhoto.url}
                  alt=""
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="flex flex-col items-center text-garden-muted">
                  <Camera size={22} />
                  <span className="text-[10px] mt-0.5">Add photo</span>
                </div>
              )}
              {photoBusy ? (
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center text-white text-[11px] font-medium">
                  Uploading…
                </div>
              ) : null}
            </button>
            <div className="flex-1 min-w-0 text-sm text-garden-muted leading-snug">
              {editing ? (
                <>
                  Tap to add or change this plant&rsquo;s photo. Photos are
                  saved under the current season ({currentSeasonGuess()}).
                  You can manage the full seasonal gallery from the plant
                  detail page.
                </>
              ) : pendingPhoto ? (
                <>
                  <div className="font-medium text-garden-ink">Photo selected</div>
                  <div className="mt-0.5">
                    Will be saved with this plant as its
                    {' '}
                    {currentSeasonGuess()} photo.
                  </div>
                  <button
                    type="button"
                    onClick={clearPendingPhoto}
                    className="mt-1 inline-flex items-center gap-1 text-[12px] text-garden-muted underline underline-offset-2"
                  >
                    <X size={12} /> Remove
                  </button>
                </>
              ) : (
                <>
                  Optional {'\u2014'} add a photo now and it will be saved
                  with the plant. You can add more photos later.
                </>
              )}
              {photoError ? (
                <div className="mt-2 text-[12px] text-red-600">{photoError}</div>
              ) : null}
            </div>
          </div>
        </div>

        <Section title="Basics" defaultOpen>
          <div className="mb-3">
            <div className="text-[11px] font-medium text-garden-muted mb-1.5">I&apos;m entering</div>
            <div className="flex rounded-full border border-garden-ink/15 p-0.5 text-xs font-medium">
              <button
                type="button"
                onClick={() => setNameEntryMode('common')}
                className={
                  'flex-1 min-h-9 rounded-full py-2 transition ' +
                  (nameEntryMode === 'common'
                    ? 'bg-garden-primary text-white shadow-sm'
                    : 'text-garden-muted')
                }
              >
                Common name
              </button>
              <button
                type="button"
                onClick={() => setNameEntryMode('scientific')}
                className={
                  'flex-1 min-h-9 rounded-full py-2 transition ' +
                  (nameEntryMode === 'scientific'
                    ? 'bg-garden-primary text-white shadow-sm'
                    : 'text-garden-muted')
                }
              >
                Scientific name
              </button>
            </div>
            <p className="mt-1.5 text-[11px] text-garden-muted leading-snug">
              {nameEntryMode === 'common'
                ? 'What you put in common name is stored as the everyday name. Add a botanical name anytime or use AI fill.'
                : 'Put the Latin / botanical name in the botanical field below. AI fill can suggest the common name and care details.'}
            </p>
          </div>
          <Field label="Common name" required>
            <input
              value={form.common_name}
              onChange={(e) => update({ common_name: e.target.value })}
              placeholder="e.g. Hydrangea"
              className="form-input"
            />
          </Field>
          <Field label="Botanical name">
            <input
              value={form.botanical_name}
              onChange={(e) => update({ botanical_name: e.target.value })}
              placeholder="e.g. Hydrangea macrophylla"
              className="form-input italic"
            />
          </Field>
          <div className="pt-1">
            <button
              type="button"
              onClick={fillFromAI}
              disabled={!canAI || aiBusy}
              className={
                'w-full min-h-11 px-4 rounded-full font-medium text-sm flex items-center justify-center gap-2 border transition ' +
                (aiBusy
                  ? 'bg-garden-primaryLight text-garden-primaryDark border-transparent'
                  : 'bg-white text-garden-primary border-garden-primary/30 hover:bg-garden-primaryLight disabled:opacity-50 disabled:cursor-not-allowed')
              }
            >
              <Sparkles size={16} />
              {aiBusy
                ? (aiProgress
                    ? `Filling ${SECTION_LABELS[aiProgress]}\u2026`
                    : 'Looking up\u2026')
                : 'Fill in what\u2019s known about this plant with AI'}
            </button>
            <p className="mt-1 text-[11px] text-garden-muted">
              Only fills fields you haven{'\u2019'}t set. You can edit anything afterward.
            </p>
          </div>
          <Field label="Nickname">
            <input
              value={form.nickname}
              onChange={(e) => update({ nickname: e.target.value })}
              placeholder="e.g. Grandma's blue one"
              className="form-input"
            />
          </Field>
          <Row>
            <Field label="Type" className="flex-1">
              <Select value={form.plant_type} onChange={(v) => update({ plant_type: v })}>
                <option value="">—</option>
                {PLANT_TYPES.map((t) => <option key={t} value={t} className="capitalize">{t}</option>)}
              </Select>
            </Field>
            <Field label="Variety" className="flex-1">
              <input
                value={form.variety}
                onChange={(e) => update({ variety: e.target.value })}
                className="form-input"
              />
            </Field>
          </Row>
        </Section>

        <Section title="Appearance">
          <SectionAiButton
            sectionKey="appearance"
            label="Appearance"
            onClick={fillOneSection}
            busy={aiBusy}
            active={aiActiveSection === 'appearance'}
            disabled={!canAI}
          />
          <Field label="Bloom color">
            <input
              value={form.bloom_color}
              onChange={(e) => update({ bloom_color: e.target.value })}
              placeholder="e.g. Pink fading to blue"
              className="form-input"
            />
          </Field>
          <Row>
            <Field label="Blooms from" className="flex-1">
              <Select value={form.bloom_month_start} onChange={(v) => update({ bloom_month_start: v })}>
                {MONTHS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
              </Select>
            </Field>
            <Field label="to" className="flex-1">
              <Select value={form.bloom_month_end} onChange={(v) => update({ bloom_month_end: v })}>
                {MONTHS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
              </Select>
            </Field>
          </Row>
          <Field label="Foliage notes">
            <textarea
              value={form.foliage_notes}
              onChange={(e) => update({ foliage_notes: e.target.value })}
              rows={4}
              className="form-input"
            />
          </Field>
          <TriToggle
            label="Evergreen?"
            value={form.is_evergreen}
            onChange={(v) => update({ is_evergreen: v })}
          />
        </Section>

        <Section title="Care">
          <SectionAiButton
            sectionKey="care"
            label="Care"
            onClick={fillOneSection}
            busy={aiBusy}
            active={aiActiveSection === 'care'}
            disabled={!canAI}
          />
          <Field label="Sun needs">
            <Select value={form.sun_needs} onChange={(v) => update({ sun_needs: v })}>
              <option value="">—</option>
              {SUN_NEEDS.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </Field>
          <Field label="Water needs">
            <Select value={form.water_needs} onChange={(v) => update({ water_needs: v })}>
              <option value="">—</option>
              {WATER_NEEDS.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </Field>
          <Field label="Soil preference">
            <input
              value={form.soil_preference}
              onChange={(e) => update({ soil_preference: e.target.value })}
              placeholder="e.g. Moist, well-drained, acidic"
              className="form-input"
            />
          </Field>
          <Row>
            <Field label="Hardiness zone" className="flex-1">
              <input
                value={form.hardiness_zone}
                onChange={(e) => update({ hardiness_zone: e.target.value })}
                placeholder="e.g. 5–9"
                className="form-input"
              />
            </Field>
          </Row>
          <Row>
            <Field label="Mature height" className="flex-1">
              <input
                value={form.mature_height}
                onChange={(e) => update({ mature_height: e.target.value })}
                placeholder="e.g. 3–6 ft"
                className="form-input"
              />
            </Field>
            <Field label="Mature width" className="flex-1">
              <input
                value={form.mature_width}
                onChange={(e) => update({ mature_width: e.target.value })}
                placeholder="e.g. 4 ft"
                className="form-input"
              />
            </Field>
          </Row>
        </Section>

        <Section title="Seasonal timing">
          <SectionAiButton
            sectionKey="seasonal"
            label="Seasonal timing"
            onClick={fillOneSection}
            busy={aiBusy}
            active={aiActiveSection === 'seasonal'}
            disabled={!canAI}
          />
          <Row>
            <Field label="Prune month" className="flex-1">
              <Select value={form.prune_month} onChange={(v) => update({ prune_month: v })}>
                {MONTHS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
              </Select>
            </Field>
            <Field label="Fertilise month" className="flex-1">
              <Select value={form.fertilise_month} onChange={(v) => update({ fertilise_month: v })}>
                {MONTHS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
              </Select>
            </Field>
          </Row>
          <Row>
            <Field label="Divide month" className="flex-1">
              <Select value={form.divide_month} onChange={(v) => update({ divide_month: v })}>
                {MONTHS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
              </Select>
            </Field>
            <Field label="Mulch month" className="flex-1">
              <Select value={form.mulch_month} onChange={(v) => update({ mulch_month: v })}>
                {MONTHS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
              </Select>
            </Field>
          </Row>
          <Field label="Prune notes">
            <textarea
              value={form.prune_notes}
              onChange={(e) => update({ prune_notes: e.target.value })}
              rows={4}
              placeholder="e.g. Cut back to two buds above ground"
              className="form-input"
            />
          </Field>
          <Row>
            <Field label="Dormancy from" className="flex-1">
              <Select value={form.dormancy_start} onChange={(v) => update({ dormancy_start: v })}>
                {MONTHS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
              </Select>
            </Field>
            <Field label="to" className="flex-1">
              <Select value={form.dormancy_end} onChange={(v) => update({ dormancy_end: v })}>
                {MONTHS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
              </Select>
            </Field>
          </Row>
          <Field label="Spring tasks">
            <textarea value={form.spring_tasks} onChange={(e) => update({ spring_tasks: e.target.value })} rows={4} className="form-input" />
          </Field>
          <Field label="Summer tasks">
            <textarea value={form.summer_tasks} onChange={(e) => update({ summer_tasks: e.target.value })} rows={4} className="form-input" />
          </Field>
          <Field label="Fall tasks">
            <textarea value={form.fall_tasks} onChange={(e) => update({ fall_tasks: e.target.value })} rows={4} className="form-input" />
          </Field>
          <Field label="Winter tasks">
            <textarea value={form.winter_tasks} onChange={(e) => update({ winter_tasks: e.target.value })} rows={4} className="form-input" />
          </Field>
        </Section>

        <Section title="In my garden">
          <SectionAiButton
            sectionKey="garden"
            label="In my garden"
            onClick={fillOneSection}
            busy={aiBusy}
            active={aiActiveSection === 'garden'}
            disabled={!canAI}
          />
          <Field label="Where it lives">
            <input
              value={form.location_notes}
              onChange={(e) => update({ location_notes: e.target.value })}
              placeholder="e.g. Front walk, left of the steps"
              className="form-input"
            />
          </Field>
          <Row>
            <Field label="Date planted" className="flex-1">
              <input
                type="date"
                value={form.date_planted}
                onChange={(e) => update({ date_planted: e.target.value })}
                className="form-input"
              />
            </Field>
            <Field label="Health" className="flex-1">
              <Select value={form.health_status} onChange={(v) => update({ health_status: v })}>
                <option value="">—</option>
                {HEALTH.map((h) => <option key={h} value={h}>{h}</option>)}
              </Select>
            </Field>
          </Row>
          <TriToggle label="Pet-safe?" value={form.is_pet_safe} onChange={(v) => update({ is_pet_safe: v })} />
          <TriToggle label="Pollinator-friendly?" value={form.is_pollinator_friendly} onChange={(v) => update({ is_pollinator_friendly: v })} />
          <TriToggle label="Native?" value={form.is_native} onChange={(v) => update({ is_native: v })} />
          <TriToggle label="Drought-tolerant?" value={form.is_drought_tolerant} onChange={(v) => update({ is_drought_tolerant: v })} />
        </Section>
      </div>

      <div className="fixed bottom-16 left-0 right-0 z-20 bg-white/95 backdrop-blur border-t border-black/5 p-3">
        <div className="mx-auto max-w-md flex gap-2">
          <PrimaryButton
            variant="secondary"
            onClick={() => navigate(-1)}
            className="flex-1"
            disabled={saving}
          >
            Cancel
          </PrimaryButton>
          <PrimaryButton
            onClick={submit}
            disabled={!canSave || saving}
            className="flex-1"
          >
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Add plant'}
          </PrimaryButton>
        </div>
      </div>

      <style>{`
        .form-input {
          width: 100%;
          min-height: 44px;
          padding: 10px 14px;
          border-radius: 12px;
          border: 1px solid rgba(31, 42, 24, 0.15);
          background: #fff;
          color: #1f2a18;
          font-size: 15px;
          outline: none;
        }
        .form-input:focus { border-color: #3B6D11; }
        textarea.form-input {
          min-height: 120px;
          padding-top: 12px;
          resize: vertical;
          field-sizing: content;
        }
      `}</style>
    </div>
  );
}

function Section({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-2xl bg-white border border-black/5 overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 min-h-12"
      >
        <span className="font-semibold text-garden-ink">{title}</span>
        <ChevronDown
          size={18}
          className={`text-garden-muted transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open ? (
        <div className="px-4 pb-4 space-y-3 border-t border-black/5 pt-3">
          {children}
        </div>
      ) : null}
    </div>
  );
}

// Small, link-style "Fill with AI" button shown inside each section so the
// user can retry just that chunk if the big button missed it (or they only
// want to fill one area). Uses fillOneSection from the parent.
function SectionAiButton({ sectionKey, label, onClick, busy, active, disabled }) {
  const isThisBusy = busy && active;
  return (
    <div>
      <button
        type="button"
        onClick={() => onClick(sectionKey)}
        disabled={disabled || busy}
        className={
          'inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 border transition ' +
          (isThisBusy
            ? 'bg-garden-primaryLight text-garden-primaryDark border-transparent'
            : 'text-garden-primary border-garden-primary/30 bg-white hover:bg-garden-primaryLight disabled:opacity-50 disabled:cursor-not-allowed')
        }
      >
        <Sparkles size={12} />
        {isThisBusy ? 'Filling\u2026' : `Fill ${label} with AI`}
      </button>
    </div>
  );
}

function Field({ label, required, children, className = '' }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs font-medium text-garden-muted">
        {label}{required ? ' *' : ''}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Row({ children }) {
  return <div className="flex gap-3">{children}</div>;
}

function Select({ value, onChange, children }) {
  return (
    <select
      value={value == null ? '' : value}
      onChange={(e) => onChange(e.target.value)}
      className="form-input capitalize"
    >
      {children}
    </select>
  );
}

function TriToggle({ label, value, onChange }) {
  const opts = [
    { v: true, l: 'Yes' },
    { v: false, l: 'No' },
    { v: null, l: "Don't know" },
  ];
  return (
    <div>
      <div className="text-xs font-medium text-garden-muted mb-1">{label}</div>
      <div className="inline-flex gap-1 p-1 rounded-full bg-garden-primaryLight">
        {opts.map((o) => {
          const active = value === o.v;
          return (
            <button
              key={String(o.v)}
              onClick={() => onChange(o.v)}
              className={
                'px-3 py-1.5 text-sm rounded-full min-h-9 font-medium ' +
                (active ? 'bg-garden-primary text-white' : 'text-garden-primaryDark')
              }
            >
              {o.l}
            </button>
          );
        })}
      </div>
    </div>
  );
}
